import { useQuery } from "@tanstack/react-query";
import { ViewBooking } from "collins-js-sdk";
import { Calendar, Clock, Edit, User } from "lucide-react";
import { useMemo, useState } from "react";
import { fetchBookingV4FromAPI } from "../../../../../APIs/Booking/GetBooking";
import { useBookingPermissions } from "../../../../../hooks/useBookingPermissions";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { VenueGroupConfig } from "../../../../../types/VenueGroup";
import { VenueConfig } from "../../../../../types/venues";
import Loading from "../../../../../utils/Loading";
import {
  formatTimeForLocale,
  getCustomerName
} from "../../../../../utils/booking";
import {
  getBookingAssignedAreaNamesAsString,
  getBookingDurationAsString,
  getBookingEndTimeAsString,
  getBookingsZoneNamesAsString,
  getBookingTimeAsString,
  getBookingType
} from "../../../../../utils/singleBooking";
import { Button } from "../../../../Shared/Buttons/Button";
import { CopyableBookingRef } from "../../../../Shared/CopyableBookingRef/CopyableBookingRef";
import CustomPill from "../../../../Shared/CustomPill/CustomPill";
import DetachedModal from "../../../../Shared/Modals/DetachedModal/DetachedModal";
import Table from "../../../../Shared/Table/Table";
import EditBooking from "../../SelectedBooking/EditBooking/EditBooking";
import {
  getSelectedBookingLabels,
  getSelectedCustomerLabels
} from "../../SelectedBooking/Labels/BookingLabels";
import { getPaymentsData } from "../../SelectedBooking/Payments/SelectedBookingPaymentsController";
import PreOrderItem from "../../SelectedBooking/PreOrders/CustomColumns/PreOrderItem";
import PreOrderTime from "../../SelectedBooking/PreOrders/CustomColumns/PreOrderTime";
import {
  getPreOrdersData,
  getPreOrderTotals
} from "../../SelectedBooking/PreOrders/SelectedBookingPreOrdersController";
import { PillsWithOverflow } from "../../Sidebar/DiaryBookingCardList/RunSheetCard/PillsWithOverflow";

type PreviewBookingModalProps = {
  booking: ViewBooking;
  venueConfig: VenueConfig;
  venueGroupConfig: VenueGroupConfig | null;
  onClose: () => void;
  onEditBooking?: () => void;
};

export const PreviewBookingModal = ({
  booking,
  venueConfig,
  venueGroupConfig,
  onClose
}: PreviewBookingModalProps) => {
  const [separateByType, setSeparateByType] = useState(false);
  const [separateBySubtype, setSeparateBySubtype] = useState(false);
  const [separateFixedPriceMenuItems, setSeparateFixedPriceMenuItems] =
    useState(false);
  const [separateByTime, setSeparateByTime] = useState(false);
  const [displayForNames, setDisplayForNames] = useState(false);
  const [showEditBooking, setShowEditBooking] = useState<boolean>(false);

  const diaryContext = useDiaryContext();
  const { selectedBookingV4 } = diaryContext;
  const { userCanManageBooking } = useBookingPermissions();

  const { data: fetchedBookingV4, isLoading: isFetchingBooking } = useQuery({
    queryKey: ["getBookingForPreview", booking.id],
    queryFn: () =>
      booking.hasPreorders && !selectedBookingV4 && booking.id
        ? fetchBookingV4FromAPI(booking.id)
        : undefined,
    enabled: booking.hasPreorders && !selectedBookingV4 && !!booking.id
  });

  const bookingV4Data = selectedBookingV4 || fetchedBookingV4;

  const customerName = getCustomerName(booking);
  const bookingTimeAsString = formatTimeForLocale(
    getBookingTimeAsString(booking)
  );
  const bookingEndTimeAsString = formatTimeForLocale(
    getBookingEndTimeAsString(booking)
  );
  const bookingDurationAsString = getBookingDurationAsString(booking);
  const bookingAssignedAreasAsString =
    getBookingAssignedAreaNamesAsString(booking);
  const bookingsZoneNamesAsString = getBookingsZoneNamesAsString({
    selectedBooking: booking,
    venueConfig
  });
  const bookingType = getBookingType({ selectedBooking: booking, venueConfig });

  const bookingLabels = venueGroupConfig
    ? getSelectedBookingLabels({
        venueConfig,
        venueGroupConfig,
        selectedBooking: booking
      })
    : [];

  const customerLabels =
    booking.customerId && venueGroupConfig
      ? getSelectedCustomerLabels({
          venueConfig,
          venueGroupConfig,
          selectedCustomer: { id: booking.customerId }
        })
      : [];

  const allLabels = [
    ...bookingLabels.map((label) => ({
      key: `booking-${label.id}`,
      label: label.label,
      backgroundColour: `#${label.colour}`,
      softenColour: false
    })),
    ...customerLabels.map((label) => ({
      key: `customer-${label.id}`,
      label: label.label,
      backgroundColour: `#${label.colour}`,
      softenColour: false
    }))
  ];

  const currency = venueConfig?.currency ?? "GBP";
  const preOrdersData = bookingV4Data
    ? getPreOrdersData(bookingV4Data, currency)
    : [];
  const preOrderTotals = bookingV4Data
    ? getPreOrderTotals(bookingV4Data, currency)
    : [];
  const paymentsData = bookingV4Data ? getPaymentsData(bookingV4Data) : [];

  const groupByType = (data: any[]) => {
    const grouped = data.reduce((acc, item) => {
      const key = item.type || "Other";
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, {});

    const result: any[] = [];
    Object.entries(grouped).forEach(([type, items]: [string, any]) => {
      result.push({
        timeCol: <PreOrderTime subType={`${type} Items`} time="" />,
        itemCol: "",
        quantity: "",
        priceCol: "",
        total: "",
        isTypeHeader: true
      });
      result.push(...items);
    });

    return result;
  };

  const groupBySubtype = (data: any[]) => {
    const sorted = data.sort((a, b) => {
      const subtypeA = a.sub_type || "Other";
      const subtypeB = b.sub_type || "Other";
      return subtypeA.localeCompare(subtypeB);
    });

    const result: any[] = [];
    let currentSubtype: string | null = null;

    sorted.forEach((item) => {
      if (item.sub_type && item.sub_type !== currentSubtype) {
        result.push({
          timeCol: <PreOrderTime subType={item.sub_type} time="" />,
          itemCol: "",
          quantity: "",
          priceCol: "",
          total: "",
          isSubtypeHeader: true
        });
        currentSubtype = item.sub_type;
      }
      result.push(item);
    });

    return result;
  };

  const groupByFixedPrice = (data: any[]) => {
    const fixedPriceItems: any[] = [];
    const regularItems: any[] = [];

    data.forEach((item) => {
      if (item.type === "menu" || item.name?.toLowerCase().includes("menu")) {
        fixedPriceItems.push(item);
      } else {
        regularItems.push(item);
      }
    });

    const result: any[] = [];
    if (fixedPriceItems.length > 0) {
      result.push({
        timeCol: <PreOrderTime subType="Fixed Price Menu Items" time="" />,
        itemCol: "",
        quantity: "",
        priceCol: "",
        total: "",
        isMenuHeader: true
      });
      result.push(...fixedPriceItems);
    }
    if (regularItems.length > 0) {
      result.push({
        timeCol: <PreOrderTime subType="À la Carte Items" time="" />,
        itemCol: "",
        quantity: "",
        priceCol: "",
        total: "",
        isMenuHeader: true
      });
      result.push(...regularItems);
    }

    return result;
  };

  const getTimeGroup = (time: string) => {
    if (!time) return "Other";

    const hour = parseInt(time.split(":")[0]);
    if (hour < 12) return "Morning";
    if (hour < 17) return "Afternoon";
    if (hour < 22) return "Evening";
    return "Other";
  };

  const groupByTime = (data: any[]) => {
    const grouped = data.reduce((acc, item) => {
      const timeKey = getTimeGroup(item.time);
      if (!acc[timeKey]) acc[timeKey] = [];
      acc[timeKey].push(item);
      return acc;
    }, {});

    const result: any[] = [];
    const timeOrder = ["Morning", "Afternoon", "Evening", "Other"];

    timeOrder.forEach((timeGroup) => {
      if (grouped[timeGroup]) {
        result.push({
          timeCol: <PreOrderTime subType={`${timeGroup} Service`} time="" />,
          itemCol: "",
          quantity: "",
          priceCol: "",
          total: "",
          isTimeHeader: true
        });
        result.push(...grouped[timeGroup]);
      }
    });

    return result;
  };

  const filterPreOrdersData = (
    data: any[],
    filters: {
      separateByType: boolean;
      separateBySubtype: boolean;
      separateFixedPriceMenuItems: boolean;
      separateByTime: boolean;
      displayForNames: boolean;
    }
  ) => {
    if (!data || data.length === 0) return data;

    let filteredData = [...data];

    if (filters.displayForNames) {
      filteredData = filteredData.map((item) => ({
        ...item,
        itemCol: (
          <PreOrderItem
            name={item.name || ""}
            description={item.description || ""}
            customer={item.for_name ? `For: ${item.for_name}` : ""}
          />
        )
      }));
    }

    if (filters.separateByType) {
      filteredData = groupByType(filteredData);
    }

    if (filters.separateBySubtype) {
      filteredData = groupBySubtype(filteredData);
    }

    if (filters.separateFixedPriceMenuItems) {
      filteredData = groupByFixedPrice(filteredData);
    }

    if (filters.separateByTime) {
      filteredData = groupByTime(filteredData);
    }

    return filteredData;
  };

  const filteredPreOrdersData = useMemo(() => {
    return filterPreOrdersData(preOrdersData, {
      separateByType,
      separateBySubtype,
      separateFixedPriceMenuItems,
      separateByTime,
      displayForNames
    });
  }, [
    preOrdersData,
    separateByType,
    separateBySubtype,
    separateFixedPriceMenuItems,
    separateByTime,
    displayForNames
  ]);

  const handleEditBooking = () => {
    diaryContext.setDiaryContext({
      selectedBooking: booking,
      editingFromPreviewModal: true
    });
    setShowEditBooking(true);
  };

  return (
    <>
      {showEditBooking && (
        <EditBooking
          booking={booking}
          venueConfig={venueConfig}
          handleClose={() => {
            setShowEditBooking(false);
            diaryContext.setDiaryContext({ editingFromPreviewModal: false });
            onClose();
          }}
        />
      )}
      <DetachedModal
        heading={
          <div className="PreviewBookingModal__header">
            <div className="PreviewBookingModal__headerLeft">
              <span className="PreviewBookingModal__venueName">
                {venueConfig.title || "Venue"}
              </span>
              <CopyableBookingRef
                reference={booking.reference}
                className="PreviewBookingModal__bookingRef"
              />
            </div>
          </div>
        }
        width="80%"
        handleClose={onClose}
      >
        <div className="PreviewBookingModal__meta flex flex-wrap gap-6 mb-4 shrink-0">
          <div className="PreviewBookingModal__metaItem flex items-center gap-2 text-[0.95rem] text-[var(--primaryColor)] font-normal">
            <Calendar className="size-5" />
            <span>
              {booking.date
                ? new Date(booking.date).toLocaleDateString()
                : "No date"}
            </span>
          </div>
          <div className="PreviewBookingModal__metaItem flex items-center gap-2 text-[0.95rem] text-[var(--primaryColor)] font-normal">
            <Clock className="size-5" />
            <span>
              {bookingTimeAsString}
              {bookingTimeAsString !== bookingEndTimeAsString &&
                ` - ${bookingEndTimeAsString}`}
              {bookingDurationAsString && ` (${bookingDurationAsString})`}
            </span>
          </div>
          <div className="PreviewBookingModal__metaItem flex items-center gap-2 text-[0.95rem] text-[var(--primaryColor)] font-normal">
            <span>{bookingAssignedAreasAsString}</span>
          </div>
          <div className="PreviewBookingModal__metaItem flex items-center gap-2 text-[0.95rem] text-[var(--primaryColor)] font-normal">
            <span>{bookingsZoneNamesAsString}</span>
          </div>
        </div>

        {allLabels.length > 0 && (
          <div className="PreviewBookingModal__tags mb-4 shrink-0">
            <PillsWithOverflow pills={allLabels} maxHeight={80} />
          </div>
        )}

        <div className="PreviewBookingModal__scrollableContent flex-1 overflow-y-auto flex flex-col gap-3 pr-6">
          <div className="PreviewBookingModal__columns grid grid-cols-1 md:grid-cols-2 gap-8 mb-4">
            <div className="PreviewBookingModal__column">
              <h3 className="mb-6 font-semibold text-[1.1rem] text-[var(--primaryColor)]">
                Customer Details
              </h3>
              <div className="PreviewBookingModal__stackedInputs flex flex-col gap-6">
                <div className="PreviewBookingModal__inputGroup flex justify-between items-center pb-3 border-b border-[var(--borderColor)]">
                  <span className="text-[0.95rem] font-normal text-[var(--primaryColor)] min-w-[120px]">
                    Customer name
                  </span>
                  <span className="input-value text-[0.95rem] text-[var(--primaryColor)] font-normal text-right flex-1">
                    {customerName ?? ""}
                  </span>
                </div>
                <div className="PreviewBookingModal__inputGroup flex justify-between items-center pb-3 border-b border-[var(--borderColor)]">
                  <span className="text-[0.95rem] font-normal text-[var(--primaryColor)] min-w-[120px]">
                    Company
                  </span>
                  <span className="input-value text-[0.95rem] text-[var(--primaryColor)] font-normal text-right flex-1">
                    {booking.company ?? ""}
                  </span>
                </div>
                <div className="PreviewBookingModal__inputGroup flex justify-between items-center pb-3 border-b border-[var(--borderColor)]">
                  <span className="text-[0.95rem] font-normal text-[var(--primaryColor)] min-w-[120px]">
                    Phone
                  </span>
                  <span className="input-value text-[0.95rem] text-[var(--primaryColor)] font-normal text-right flex-1">
                    {booking.phone ?? ""}
                  </span>
                </div>
              </div>
            </div>

            <div className="PreviewBookingModal__column">
              <h3 className="mb-6 font-semibold text-[1.1rem] text-[var(--primaryColor)]">
                Booking Details
              </h3>
              <div className="PreviewBookingModal__bookingInfo flex flex-col gap-6">
                <div className="PreviewBookingModal__guests flex justify-between items-center pb-3 border-b border-[var(--borderColor)]">
                  <span className="label text-[0.95rem] text-[var(--primaryColor)] font-normal">
                    Guests
                  </span>
                  <div className="value flex items-center gap-2 text-[0.95rem] font-normal text-[var(--primaryColor)]">
                    <User className="PreviewBookingModal__guestIcon size-5" />
                    <span>{booking.actualGuests || "2"}</span>
                  </div>
                </div>
                {bookingType && (
                  <div className="PreviewBookingModal__bookingType flex justify-between items-center pb-3 border-b border-[var(--borderColor)]">
                    <span className="label text-[0.95rem] text-[var(--primaryColor)] font-normal">
                      Booking type
                    </span>
                    <CustomPill
                      label={bookingType.name}
                      backgroundColour={bookingType.color}
                      softenColour={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="PreviewBookingModal__preOrders">
            <h3 className="mb-6 font-semibold text-[1.1rem] text-[var(--primaryColor)]">
              Pre-Orders
            </h3>

            <div className="PreviewBookingModal__options flex flex-wrap gap-6 mb-4 p-0">
              <div className="PreviewBookingModal__option">
                <input
                  type="checkbox"
                  checked={separateByType}
                  onChange={(e) => setSeparateByType(e.target.checked)}
                />
                <label>Separate by type</label>
              </div>
              <div className="PreviewBookingModal__option">
                <input
                  type="checkbox"
                  checked={separateBySubtype}
                  onChange={(e) => setSeparateBySubtype(e.target.checked)}
                />
                <label>Separate by subtype</label>
              </div>
              <div className="PreviewBookingModal__option">
                <input
                  type="checkbox"
                  checked={separateFixedPriceMenuItems}
                  onChange={(e) =>
                    setSeparateFixedPriceMenuItems(e.target.checked)
                  }
                />
                <label>Separate fixed price menu items</label>
              </div>
              <div className="PreviewBookingModal__option">
                <input
                  type="checkbox"
                  checked={separateByTime}
                  onChange={(e) => setSeparateByTime(e.target.checked)}
                />
                <label>Separate by time</label>
              </div>
              <div className="PreviewBookingModal__option">
                <input
                  type="checkbox"
                  checked={displayForNames}
                  onChange={(e) => setDisplayForNames(e.target.checked)}
                />
                <label>Display for names</label>
              </div>
            </div>

            {isFetchingBooking && booking.hasPreorders ? (
              <Loading />
            ) : bookingV4Data && filteredPreOrdersData.length > 0 ? (
              <div className="PreviewBookingModal__preOrdersTable">
                <Table
                  columns={[
                    {
                      dataNameJSX: "timeCol",
                      caption: "Time"
                    },
                    {
                      dataNameJSX: "itemCol",
                      caption: "Item"
                    },
                    {
                      dataName: "quantity",
                      caption: "Quantity",
                      className: "BookingPayments-TableColumnQuantity",
                      align: "Right"
                    },
                    {
                      dataNameJSX: "priceCol",
                      caption: "Price",
                      className: "BookingPayments-TableColumnMoney",
                      align: "Right"
                    },
                    {
                      dataName: "total",
                      caption: "Total",
                      className: "BookingPayments-TableColumnMoney",
                      align: "Right"
                    }
                  ]}
                  data={filteredPreOrdersData}
                />
                {preOrderTotals.length > 0 && (
                  <div className="PreviewBookingModal__totals">
                    <Table
                      columns={[
                        { dataName: "description", caption: "" },
                        { dataName: "total", caption: "", align: "Right" }
                      ]}
                      data={preOrderTotals}
                    />
                  </div>
                )}
              </div>
            ) : (
              <span className="NoRecords">
                No pre-orders have been added to this booking.
              </span>
            )}
          </div>

          {bookingV4Data && paymentsData.length > 0 && (
            <div className="PreviewBookingModal__payments">
              <div className="PreviewBookingModal__divider" />
              <h3>Payments & Deposits</h3>
              <div className="PreviewBookingModal__paymentsTable">
                <Table
                  columns={[
                    {
                      dataName: "displayDate",
                      caption: "Added"
                    },
                    {
                      dataName: "displayType",
                      caption: "Type"
                    },
                    {
                      dataName: "displayAmount",
                      caption: "Amount",
                      className: "BookingPayments-TableColumnAmount",
                      align: "Right"
                    },
                    {
                      dataName: "displayStatus",
                      leftIconDataName: "displayStatusIcon",
                      caption: "Status",
                      className: "BookingPayments-TableColumnStatus",
                      align: "Right"
                    }
                  ]}
                  data={paymentsData}
                />
              </div>
            </div>
          )}
        </div>

        <div className="PreviewBookingModal__footer flex justify-between items-center gap-4 pt-4 pb-6 border-t border-[var(--borderColor)] shrink-0 bg-[var(--panelBackground)]">
          <Button className="btn btn-cancel" onClick={onClose}>
            Cancel
          </Button>
          <div className="footer-right flex gap-4">
            {userCanManageBooking && (
              <Button
                className="btn btn-primary"
                onClick={handleEditBooking}
                icon={<Edit className="size-5" />}
              >
                Edit Booking
              </Button>
            )}
          </div>
        </div>
      </DetachedModal>
    </>
  );
};
